/**
 * Date formatting utilities for consistent European date format (dd-MM-yyyy) across the application.
 */

const EUROPEAN_LOCALE = "nl-NL";

/**
 * Formats a date to European format: dd-MM-yyyy
 * @param date - Date object, string, null, or undefined
 * @returns Formatted date string or "-" if date is null/undefined
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "-";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "-";

  return new Intl.DateTimeFormat(EUROPEAN_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateObj);
}

/**
 * Formats a date to European format with time: dd-MM-yyyy HH:mm
 * @param date - Date object, string, null, or undefined
 * @returns Formatted date-time string or "-" if date is null/undefined
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return "-";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "-";

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * Formats a date to YYYY-MM-DD string format for internal use (APIs, etc.)
 * @param date - Date object or string
 * @returns Formatted date string in YYYY-MM-DD format or empty string if invalid
 */
export function formatDateISO(date: Date | string | null | undefined): string {
  if (!date) return "";

  if (typeof date === "string") {
    // If already in YYYY-MM-DD format or ISO string, extract date part
    return date.split("T")[0];
  }

  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Formats a date to dd-MM-yyyy string format for display
 * This is an alias for formatDate for backwards compatibility
 * @param date - Date object or string
 * @returns Formatted date string in dd-MM-yyyy format or empty string if invalid
 */
export function formatDateToString(
  date: Date | string | null | undefined,
): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "";

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();

  return `${day}-${month}-${year}`;
}

/**
 * Formats a date to dd-MM-yyyy HH:mm string format for display
 * @param date - Date object or string
 * @returns Formatted date-time string in dd-MM-yyyy HH:mm format or empty string if invalid
 */
export function formatDateTimeToString(
  date: Date | string | null | undefined,
): string {
  if (!date) return "";

  if (typeof date === "string") {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "";

    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * Formats a date to Dutch long format: "8 januari 2026 12:34"
 * @param date - Date object, string, null, or undefined
 * @returns Formatted date string in Dutch long format or empty string if invalid
 */
export function formatDateTimeDutch(date: Date | string | null | undefined): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "";

  return new Intl.DateTimeFormat(EUROPEAN_LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dateObj);
}
